<template>
  <b-modal
    ref="modal"
    modal-class="m-doc-editor"
    body-class="m-doc-editor__body"
    hide-header
    hide-footer
    no-close-on-esc
    no-close-on-backdrop
    @shown="resetDraft"
    @hide="closeHandler"
    @hidden="onHidden"
  >
    <SpreadsheetDocEditor
      v-if="isOpen"
      ref="editor"
      :name="name"
      :delta="delta"
      :saved="saved"
      back-label="Back to Files"
      hide-ai-import
      :resolve-save-folder="resolveSaveFolder"
      :picker-location="pickerLocation"
      :document-formats="DOCUMENT_FORMATS"
      @update-name="name = $event"
      @update-delta="onDeltaUpdate"
      @update-saved="saved = $event"
      @clean="hasUnsavedChanges = false"
      @back="back"
      @hook:mounted="openItem"
    />
  </b-modal>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import Swal from 'sweetalert2'
import mixinModal from './mixin'
import FileBrowserApi from '@frontend/services/api/file-browser'
import { getOrCreateRootFolderId } from '../root-folder'

const { mapActions } = createNamespacedHelpers('fileBrowser')

const COPY_DOCUMENTS_FOLDER_NAME = 'Copy documents'
const DOCUMENT_FORMATS = ['docx', 'doc', 'txt']

export default {
  name: 'DocEditorModal',
  components: {
    // Lazy-loaded so Quill, docx and mammoth stay out of the Assets bundle.
    SpreadsheetDocEditor: () => import('@frontend/components/modules/layouts-page/EditorControl/components/SpreadsheetDocEditor.vue'),
  },
  mixins: [mixinModal],
  props: {
    payload: {
      type: Object,
      default: () => ({}),
    },
  },
  data: () => ({
    isOpen: false, name: '', delta: null, saved: null, hasUnsavedChanges: false, DOCUMENT_FORMATS,
  }),
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.warnBeforeUnload)
  },
  methods: {
    ...mapActions(['fetchList', 'fetchBreadcrumbs']),
    resetDraft() {
      this.name = this.payload.item ? this.payload.item.name : 'Untitled doc'
      this.delta = null
      this.saved = null
      this.hasUnsavedChanges = false
      this.isOpen = true
      window.addEventListener('beforeunload', this.warnBeforeUnload)
    },
    async openItem() {
      if (!this.payload.item) return
      await this.$nextTick()
      if (!await this.$refs.editor.openSaved(this.payload.item)) this.close()
    },
    onDeltaUpdate(delta) {
      this.delta = delta
      this.hasUnsavedChanges = true
    },
    async back() {
      if (this.hasUnsavedChanges && !await this.confirmDiscard()) return
      this.close()
      this.fetchList()
    },
    onHidden() {
      this.isOpen = false
      window.removeEventListener('beforeunload', this.warnBeforeUnload)
    },
    warnBeforeUnload(event) {
      if (!this.hasUnsavedChanges) return
      event.preventDefault()
      event.returnValue = ''
    },
    async confirmDiscard() {
      const { isConfirmed } = await Swal.fire({
        icon: 'warning',
        text: 'You have unsaved changes. Leave without saving?',
        showCancelButton: true,
        confirmButtonText: 'Leave',
        cancelButtonText: 'Stay',
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-secondary ml-2',
        },
      })
      return isConfirmed
    },
    async resolveSaveFolder(sourceFolderId) {
      const id = sourceFolderId || this.payload.folderId || await getOrCreateRootFolderId(COPY_DOCUMENTS_FOLDER_NAME)
      const { data } = await FileBrowserApi.getFolderName(id)
      return { id, label: data.name }
    },
    async pickerLocation() {
      await this.fetchBreadcrumbs()
      const breadcrumbs = this.$store.getters['fileBrowser/breadcrumbs'].map(({ id, text }) => ({ id, text }))
      return breadcrumbs.length ? { id: breadcrumbs[breadcrumbs.length - 1].id, breadcrumbs } : null
    },
  },
}
</script>

<style scoped lang="scss">
::v-deep .m-doc-editor .modal-dialog {
  max-width: 100%;
  margin: 0;
  position: fixed;
  inset: 0;
  display: flex;

  .modal-content {
    height: 100vh;
    border: 0;
    border-radius: 0;
  }
}
::v-deep .m-doc-editor :focus-visible {
  outline: 2px solid transparent;
  outline-offset: 2px;
  box-shadow: 0 0 0 2px #fafafa, 0 0 0 4px hsl(162 73% 35%);
}
::v-deep .m-doc-editor {
  --foreground: 222.2 47.4% 11.2%;

  .doc-back, .doc-menu > button, .doc-save, .doc-icon-button {
    color: hsl(var(--foreground));
  }
}
::v-deep .m-doc-editor__body {
  display: flex;
  padding: 0;
}
</style>
